import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { TEST_IDS } from "@/constants/testIds";
import { fetchMnemonic, saveMnemonic, deleteMnemonic } from "@/lib/api";

// NOTE: the parent passes key={kanji} so this component remounts when the
// drawer's kanji changes — that resets the editing state and seeds the
// textarea from the freshly-fetched saved mnemonic.
export default function MnemonicEditor({ kanji }) {
  const qc = useQueryClient();

  const mnemonicQ = useQuery({
    queryKey: ["mnemonic", kanji],
    queryFn: () => fetchMnemonic(kanji),
    enabled: !!kanji,
  });

  const savedText = mnemonicQ.data?.user_mnemonic || "";
  // `draft` is null until the user starts editing; the textarea then shows
  // savedText. Once draft !== null, the user's in-progress edit is shown.
  const [draft, setDraft] = useState(null);
  const editing = draft !== null;
  const textareaValue = draft ?? savedText;

  const saveM = useMutation({
    mutationFn: () => saveMnemonic(kanji, textareaValue.trim()),
    onSuccess: () => {
      toast.success("Mnemonic saved");
      setDraft(null);
      qc.invalidateQueries({ queryKey: ["mnemonic", kanji] });
    },
    onError: () => toast.error("Could not save"),
  });

  const deleteM = useMutation({
    mutationFn: () => deleteMnemonic(kanji),
    onSuccess: () => {
      toast.success("Deleted");
      setDraft(null);
      qc.invalidateQueries({ queryKey: ["mnemonic", kanji] });
    },
  });

  if (!kanji) return null;
  const data = mnemonicQ.data;
  const hasUser = !!data?.user_mnemonic;

  return (
    <div data-testid={TEST_IDS.mneEditor} className="space-y-4">
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] uppercase tracking-[0.3em] text-sumiSoft font-sans">
            your story
          </p>
          {hasUser && !editing && (
            <button
              onClick={() => setDraft(savedText)}
              className="text-[10px] uppercase tracking-[0.25em] text-shu hover:text-sumi font-sans"
            >
              edit
            </button>
          )}
        </div>

        {!editing && hasUser && (
          <blockquote
            className="border-l-2 border-shu pl-4 italic font-serif text-base text-sumi leading-relaxed whitespace-pre-wrap"
          >
            {data.user_mnemonic}
          </blockquote>
        )}

        {(editing || !hasUser) && (
          <div className="flex flex-col gap-2">
            <textarea
              data-testid={TEST_IDS.mneTextarea}
              value={textareaValue}
              onChange={(e) => setDraft(e.target.value)}
              onFocus={() => { if (draft === null) setDraft(savedText); }}
              placeholder="write your own memory story… use the component meanings."
              rows={4}
              maxLength={1000}
              className="w-full bg-washi border border-borderDefault px-3 py-2 font-serif text-base text-sumi outline-none focus:border-sumi resize-y"
            />
            <div className="flex items-center gap-2 flex-wrap">
              <button
                data-testid={TEST_IDS.mneSave}
                onClick={() => textareaValue.trim() && saveM.mutate()}
                disabled={!textareaValue.trim() || saveM.isPending}
                className="flex items-center gap-1.5 text-xs font-sans uppercase tracking-[0.25em] text-washi bg-sumi px-4 py-2 hover:bg-shu transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {saveM.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save
              </button>
              {hasUser && (
                <button
                  data-testid={TEST_IDS.mneDelete}
                  onClick={() => deleteM.mutate()}
                  className="flex items-center gap-1.5 text-xs font-sans uppercase tracking-[0.25em] text-sumi border border-borderDefault px-4 py-2 hover:border-shu hover:text-shu transition-colors"
                >
                  <Trash2 size={14} /> Delete
                </button>
              )}
              {editing && hasUser && (
                <button
                  onClick={() => setDraft(null)}
                  className="text-xs font-sans uppercase tracking-[0.25em] text-sumiSoft hover:text-sumi"
                >
                  cancel
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
